/**
 * Team members popup navigation
 *
 * Enhances .ws-popup instances created from the Team block so that
 * the user can navigate between members using arrows inside the popup.
 *
 * Relies on:
 * - Each team item having data-team-index (0-based)
 * - Each popup template root having data-team-block-id + data-team-index
 * - window.wsPopup.openFromTemplate() from popup.js
 */
(function () {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return;
    }

    function findTeamContextFromPopup(popupEl) {
        if (!popupEl) return null;

        var popup = popupEl.closest('.ws-popup');
        if (!popup) return null;

        var blockId = popup.getAttribute('data-team-block-id');
        if (!blockId) return null;

        // Raw index on the PHP side (includes members without popups)
        var rawIndex = parseInt(popup.getAttribute('data-team-index') || '0', 10);
        if (isNaN(rawIndex)) rawIndex = 0;

        // Find original team block container
        var block = document.querySelector('.team[data-team-block-id="' + blockId.replace(/"/g, '\\"') + '"]');
        if (!block) return null;

        // Only consider members that actually have a popup template
        var allItems = Array.prototype.slice.call(
            block.querySelectorAll('.team__item[data-team-index]')
        );
        var items = allItems.filter(function (item) {
            try {
                return !!item.querySelector('template.ws-popup-template');
            } catch (e) {
                return false;
            }
        });
        if (!items.length) return null;

        // Position in the filtered popup list
        var index = items.findIndex(function (item) {
            var idx = parseInt(item.getAttribute('data-team-index') || '0', 10);
            return idx === rawIndex;
        });
        if (index < 0) index = 0;

        return {
            popup: popup,
            block: block,
            items: items,
            index: index,      // position among members that have popups
            rawIndex: rawIndex,
            blockId: blockId
        };
    }

    function updateArrowStates(popupEl, ctx) {
        if (!popupEl) return;
        ctx = ctx || findTeamContextFromPopup(popupEl);
        if (!ctx) return;

        var prevBtn = popupEl.querySelector('.team__popup-arrow--prev');
        var nextBtn = popupEl.querySelector('.team__popup-arrow--next');
        if (!prevBtn || !nextBtn) return;

        var isFirst = ctx.index === 0;
        var isLast = ctx.index === (ctx.items.length - 1);

        [prevBtn, nextBtn].forEach(function (btn) {
            btn.removeAttribute('disabled');
            btn.setAttribute('aria-disabled', 'false');
            btn.classList.remove('is-disabled');
        });

        if (isFirst) {
            prevBtn.disabled = true;
            prevBtn.setAttribute('aria-disabled', 'true');
            prevBtn.classList.add('is-disabled');
        }
        if (isLast) {
            nextBtn.disabled = true;
            nextBtn.setAttribute('aria-disabled', 'true');
            nextBtn.classList.add('is-disabled');
        }
    }

    function switchMember(popupEl, direction) {
        var ctx = findTeamContextFromPopup(popupEl);
        if (!ctx) return;

        var total = ctx.items.length;
        if (!total) return;

        var nextIndex = ctx.index + direction;
        // No wrap-around: stop at ends
        if (nextIndex < 0 || nextIndex >= total) {
            return;
        }

        var nextItem = ctx.items.find(function (item) {
            var idx = parseInt(item.getAttribute('data-team-index') || '0', 10);
            return idx === nextIndex;
        }) || ctx.items[nextIndex];

        if (!nextItem || !window.wsPopup || typeof window.wsPopup.openFromTemplate !== 'function') {
            return;
        }

        var overlay = popupEl.closest('.ws-popup-overlay');
        if (!overlay) {
            // As a fallback, just open the next popup without fancy transition
            window.wsPopup.openFromTemplate(nextItem);
            return;
        }

        if (overlay.classList.contains('is-closing')) {
            return;
        }

        // Start fade-out of current overlay (matches popup.js behavior)
        overlay.classList.remove('is-active');
        overlay.classList.add('is-closing');

        // Remember navigation direction for the next popup animation
        try {
            if (document.body) {
                document.body.setAttribute('data-team-popup-direction', direction > 0 ? 'next' : 'prev');
            }
        } catch (e) {}

        var done = false;
        function cleanup() {
            if (done) return;
            done = true;
            if (overlay && overlay.parentNode) {
                overlay.parentNode.removeChild(overlay);
            }
            // Slight delay so the next popup fade-in is more noticeable
            setTimeout(function () {
                try {
                    window.wsPopup.openFromTemplate(nextItem);
                } catch (e) {
                    // no-op
                }
            }, 30);
        }

        overlay.addEventListener('transitionend', function (e) {
            if (e.target === overlay) {
                cleanup();
            }
        }, { once: true });

        // Safety timeout in case transitionend doesn't fire
        setTimeout(cleanup, 400);
    }

    // Delegate click handling for navigation arrows inside any team popup
    document.addEventListener('click', function (e) {
        var target = e.target;
        if (!target) return;

        var btn = target.closest('.team__popup-arrow');
        if (!btn) return;

        var popup = btn.closest('.ws-popup');
        if (!popup) return;

        e.preventDefault();

        var isNext = btn.classList.contains('team__popup-arrow--next');
        switchMember(popup, isNext ? 1 : -1);
    });

    // Observe new popups being added so we can set the correct arrow disabled state
    try {
        var observer = new MutationObserver(function (mutations) {
            mutations.forEach(function (mutation) {
                if (!mutation.addedNodes) return;
                mutation.addedNodes.forEach(function (node) {
                    if (!(node instanceof HTMLElement)) return;

                    var popup = null;
                    if (node.matches && node.matches('.ws-popup[data-team-block-id]')) {
                        popup = node;
                    } else if (node.querySelector) {
                        popup = node.querySelector('.ws-popup[data-team-block-id]');
                    }

                    if (!popup) return;

                    // Apply directional animation class based on last navigation intent, if any
                    try {
                        var body = document.body;
                        if (body) {
                            var dir = body.getAttribute('data-team-popup-direction');
                            if (dir === 'next' || dir === 'prev') {
                                var overlay = popup.closest('.ws-popup-overlay');
                                if (overlay) {
                                    overlay.classList.add(
                                        dir === 'next'
                                            ? 'ws-popup-overlay--from-next'
                                            : 'ws-popup-overlay--from-prev'
                                    );
                                }
                                body.removeAttribute('data-team-popup-direction');
                            }
                        }
                    } catch (e) {}

                    updateArrowStates(popup);
                });
            });
        });

        observer.observe(document.body, { childList: true, subtree: true });
    } catch (e) {
        // no-op
    }
})();



